import { useEffect, useState } from "react";
import { api } from "../api";
import { Btn, Input, Spinner } from "../components/Shared";

/**
 * What this facility charges each kind of customer, as a percentage.
 *
 * The rate lives on the affiliation rather than the group because a facility
 * does not have one tax position — a private company and a state institution
 * are taxed differently, and which one a customer is, is exactly what an
 * affiliation records.
 *
 * Group-admin only, like the price list it belongs to. An empty rate is not
 * zero: a quotation asking for VAT against an affiliation with no rate is
 * refused, because a quotation that silently omits tax the facility owes is not
 * noticed until somebody has signed it.
 */
export default function AffiliationVatPanel({ groupId, canEdit }) {
  const [rows, setRows]     = useState(null);
  const [draft, setDraft]   = useState({});
  const [busy, setBusy]     = useState(null);
  const [error, setError]   = useState(null);
  const [saved, setSaved]   = useState(null);

  useEffect(() => {
    if (!groupId) return;
    let live = true;
    setRows(null); setError(null); setDraft({});
    api.get(`/api/affiliations?group_id=${groupId}`)
      .then(r => live && setRows(r))
      .catch(e => live && setError(e.message || "Could not load affiliations"));
    return () => { live = false; };
  }, [groupId]);

  const save = async a => {
    setBusy(a.id); setError(null); setSaved(null);
    try {
      const value = draft[a.id];
      const updated = await api.patch(
        `/api/groups/${groupId}/affiliations/${a.id}`,
        { vat_rate: value === "" ? null : value });
      setRows(rs => rs.map(r => (r.id === a.id ? updated : r)));
      setDraft(d => { const { [a.id]: _drop, ...rest } = d; return rest; });
      setSaved(a.id);
    } catch (e) {
      setError(e.message || "Could not save the rate");
    } finally { setBusy(null); }
  };

  if (error && rows === null) {
    return <p style={{ margin: 0, fontSize: 13, color: "#b91c1c" }}>{error}</p>;
  }
  if (rows === null) return <Spinner />;
  if (!rows.length) {
    // Since affiliations are created by a group's own price import, a group that
    // has imported nothing has none — and an empty table would read as a bug.
    return (
      <p style={{ margin: 0, fontSize: 13, color: "#6b7280" }}>
        This group has no affiliations yet. They are created by importing a price list, which is
        also what decides which kinds of customer this facility has.
      </p>
    );
  }

  return (
    <div>
      {error && (
        <p role="alert" style={{ margin: "0 0 10px", fontSize: 13, color: "#b91c1c" }}>{error}</p>
      )}
      <p style={{ margin: "0 0 10px", fontSize: 12, color: "#6b7280" }}>
        Leave a rate empty if VAT does not apply to that kind of customer — a quotation asking for
        VAT will then say so rather than charging nothing.
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {rows.map(a => {
          const value = draft[a.id] ?? (a.vat_rate ?? "");
          const dirty = draft[a.id] !== undefined && String(draft[a.id]) !== String(a.vat_rate ?? "");
          return (
            <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 10,
                                     flexWrap: "wrap", fontSize: 13 }}>
              <strong style={{ minWidth: 150 }}>{a.label || a.code}</strong>
              <span style={{ color: "#6b7280", fontFamily: "'JetBrains Mono', monospace",
                             fontSize: 12 }}>{a.code}</span>
              <span style={{ flex: 1 }} />
              <Input type="number" min="0" max="100" step="0.01"
                     aria-label={`VAT rate for ${a.label || a.code}`}
                     disabled={!canEdit || busy === a.id}
                     value={value}
                     onChange={e => setDraft(d => ({ ...d, [a.id]: e.target.value }))}
                     style={{ width: 90 }} />
              <span style={{ color: "#6b7280" }}>%</span>
              {canEdit && dirty && (
                <Btn onClick={() => save(a)} disabled={busy === a.id}>
                  {busy === a.id ? "Saving…" : "Save"}
                </Btn>
              )}
              {saved === a.id && !dirty && (
                <span style={{ fontSize: 12, color: "#059669" }}>Saved</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
